type SectionLike = {
	id: string;
	parentSectionId: string | null;
	order: number;
};
type ItemLike = {
	id: string;
	sectionId: string | null;
	parentItemId: string | null;
	order: number;
};

/**
 * Splits a list into children keyed by parent id and roots. An entry whose
 * parent is not in the list is a root, so nothing is dropped.
 */
function bucketByParent<T extends { id: string }>(
	list: readonly T[],
	parentOf: (entry: T) => string | null,
) {
	const ids = new Set(list.map((entry) => entry.id));
	const children = new Map<string, T[]>();
	const roots: T[] = [];
	for (const entry of list) {
		const parent = parentOf(entry);
		if (parent !== null && ids.has(parent)) {
			const siblings = children.get(parent) ?? [];
			siblings.push(entry);
			children.set(parent, siblings);
		} else {
			roots.push(entry);
		}
	}
	return { children, roots };
}

/** A root section that owns child sections is a heading; any other is a plain section. */
export type DisplaySectionKind = "heading" | "section";

/** One section in display order, with where it sits and its own ordered items. */
export type DisplaySection<S, I> = {
	section: S;
	kind: DisplaySectionKind;
	/** The heading this section sits under, or null for headings and loose sections. */
	headingId: string | null;
	/** Every ancestor section id, outermost first. */
	ancestorIds: string[];
	/** The section's own items in display order. */
	items: I[];
};

/** The result of `orderProcedureForDisplay`: sections, root items and every item in reading order. */
export type ProcedureDisplayOrder<S, I> = {
	sections: DisplaySection<S, I>[];
	/** Items outside any section. They always display last. */
	rootItems: I[];
	/** Every item in the order a reader sees it: section items, then root items. */
	items: I[];
};

/**
 * Orders a Procedure's structure the way the editor and the Work Order
 * execution view show it. Every reader of a Procedure's structure should use
 * this instead of sorting `order` fields itself.
 *
 * 1. Root sections without child sections (loose sections), by `order`.
 * 2. Each heading (a root section with child sections), by `order`, followed
 *    depth-first by its descendants with each level sorted by `order`.
 * 3. Items inside each section by `order`; items nested under a parent item
 *    follow that parent depth-first.
 * 4. Items outside any section, by `order`, last.
 *
 * Root sections are not sorted by `order` alone: older data numbers headings
 * before loose sections while the editor shows loose sections first, so the
 * shape decides the group. Sections or items that reference a missing parent
 * are treated as roots so nothing is dropped.
 */
export function orderProcedureForDisplay<
	S extends SectionLike,
	I extends ItemLike,
>(procedure: {
	sections: readonly S[];
	items: readonly I[];
}): ProcedureDisplayOrder<S, I> {
	const bySiblingOrder = (left: { order: number }, right: { order: number }) =>
		left.order - right.order;
	const sectionIds = new Set(procedure.sections.map((section) => section.id));
	const itemsBySection = new Map<string | null, I[]>();
	for (const item of procedure.items) {
		const key =
			item.sectionId !== null && sectionIds.has(item.sectionId)
				? item.sectionId
				: null;
		const bucket = itemsBySection.get(key) ?? [];
		bucket.push(item);
		itemsBySection.set(key, bucket);
	}

	const orderItems = (bucket: readonly I[]): I[] => {
		const { children, roots: tops } = bucketByParent(
			bucket,
			(item) => item.parentItemId,
		);
		const result: I[] = [];
		const seen = new Set<string>();
		const visit = (item: I) => {
			if (seen.has(item.id)) return;
			seen.add(item.id);
			result.push(item);
			for (const child of [...(children.get(item.id) ?? [])].sort(
				bySiblingOrder,
			))
				visit(child);
		};
		for (const item of [...tops].sort(bySiblingOrder)) visit(item);
		// Items that only reference each other in a cycle are never reached above.
		for (const item of [...bucket].sort(bySiblingOrder)) visit(item);
		return result;
	};

	const { children: childrenOf, roots } = bucketByParent(
		procedure.sections,
		(section) => section.parentSectionId,
	);
	const sortedChildren = (id: string) =>
		[...(childrenOf.get(id) ?? [])].sort(bySiblingOrder);
	const sortedRoots = [...roots].sort(bySiblingOrder);

	const sections: DisplaySection<S, I>[] = [];
	const entry = (
		section: S,
		kind: DisplaySectionKind,
		headingId: string | null,
		ancestorIds: string[],
	): DisplaySection<S, I> => ({
		section,
		kind,
		headingId,
		ancestorIds,
		items: orderItems(itemsBySection.get(section.id) ?? []),
	});
	const visitedSections = new Set<string>();
	const addDescendants = (
		parent: S,
		headingId: string,
		ancestorIds: string[],
	) => {
		for (const child of sortedChildren(parent.id)) {
			if (visitedSections.has(child.id)) continue;
			visitedSections.add(child.id);
			sections.push(entry(child, "section", headingId, ancestorIds));
			addDescendants(child, headingId, [...ancestorIds, child.id]);
		}
	};

	for (const section of sortedRoots) {
		if (sortedChildren(section.id).length > 0) continue;
		visitedSections.add(section.id);
		sections.push(entry(section, "section", null, []));
	}
	for (const heading of sortedRoots) {
		if (sortedChildren(heading.id).length === 0) continue;
		visitedSections.add(heading.id);
		sections.push(entry(heading, "heading", null, []));
		addDescendants(heading, heading.id, [heading.id]);
	}

	// Only a cyclic hierarchy, which the schema rejects, leaves sections
	// unreached. Keep their items rather than silently dropping steps.
	for (const section of [...procedure.sections].sort(bySiblingOrder)) {
		if (visitedSections.has(section.id)) continue;
		visitedSections.add(section.id);
		sections.push(entry(section, "section", null, []));
	}

	const rootItems = orderItems(itemsBySection.get(null) ?? []);
	return {
		sections,
		rootItems,
		items: [...sections.flatMap((section) => section.items), ...rootItems],
	};
}
